import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { bibShape, shelfItemShape, userIdentityShape, ugcRatingShape } from '@bibliocommons/bc-prop-types';
import Context from '@bibliocommons/context';
import { Lock } from '@bibliocommons/deprecated-base-icons';
import { bindAll } from '@bibliocommons/utils-react';

import AvailabilityBibBlock from 'app/components/shared/AvailabilityBibBlock';
import BibBrief from 'app/components/shared/BibBrief';
import ItemFormat from 'app/components/shared/ItemFormat';
import { MATERIAL_TYPE_DIGITAL } from 'app/constants/BibConstants';
import RatingsInfo from '../RatingsInfo';

import './BibBriefInfo.scss';

export default class ShelfListItemBibBriefInfo extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  trackTitleClick() {
    const { bib, trackContentClick } = this.props;
    const bibId = bib.get('id');
    const bibTitle = bib.getIn(['briefInfo', 'title']);

    trackContentClick({
      bibId,
      metadata: {
        metadata_category_type: 'bibliographic_details_title',
        metadata_value: bibTitle
      },
      ui: {
        ui_container_type: 'item',
        ui_component_type: 'link-heading',
        ui_component_label: bibTitle
      }
    });
  }

  trackAuthorClick({ author }) {
    const { bib, trackCatalogExplore } = this.props;
    const bibId = bib.get('id');
    const metadata = {
      metadata_category_type: 'bibliographic_details_creators',
      metadata_value: author
    };
    const ui = {
      ui_container_type: 'item',
      ui_component_type: 'link-text',
      ui_component_label: author
    };

    trackCatalogExplore({ bibId, metadata, ui });
  }

  renderAvailabilityInfo() {
    const { bib, availabilityDetailActions, currentLibrary } = this.props;
    const availabilityStatus = bib.get('availability');
    const isDigitalBib = bib.getIn(['availability', 'bibType']) === MATERIAL_TYPE_DIGITAL;
    const isCurrentLibraryBib = bib.get('sourceLibId') === currentLibrary.get('id');

    // show availability information for digital bibs and bibs that belong to the current library.
    if (bib.get('briefInfo') && availabilityStatus && (isDigitalBib || isCurrentLibraryBib)) {
      return (
        <div className="shelf-availability-info">
          <AvailabilityBibBlock
            bib={bib}
            fetchDigitalAvailability={availabilityDetailActions.getDigitalAvailability}
            hasAvailabilityStatus
            hasFetchDigitalAvailabilityButton
            hasHoldCounts
            hasPhysicalAvailabilityLink
            openAvailabilityOverlay={availabilityDetailActions.openAvailability}
            trackViewAvailabilityClick={this.props.handleAvailabilityLinkClick}
            trackViewDigitalResourceClick={() => null}
          />
        </div>
      );
    }

    return null;
  }

  renderPrivacyLock() {
    const { item, owner } = this.props;
    if (!item.get('privateItem') || !owner.get('loggedIn')) {
      return null;
    }

    return <Lock className="privacy-lock" />;
  }

  renderScreenReaderTitle({ title, format }) {
    const { item, owner } = this.props;
    const titleMessage = <FormattedMessage id="ada_link_to_item_page" values={{ title, format }} />;

    if (!owner.get('loggedIn')) {
      return titleMessage;
    }

    if (item.get('privateItem')) {
      return (
        <span>
          {titleMessage}
          <FormattedMessage id="item_is_private" />
        </span>
      );
    }

    return (
      <span>
        {titleMessage}
        <FormattedMessage id="item_is_shared" />
      </span>
    );
  }

  render() {
    const { bib, item, owner, shelfName, ownerRatingForBib } = this.props;

    const briefInfo = bib.get('briefInfo');

    return (
      <div className="cp-shelf-list-item-bib-brief-info">
        <BibBrief
          bib={briefInfo}
          handleBibTitleClick={this.trackTitleClick}
          renderAuthorsAsLink={!this.context.mobileApp}
          renderTitleAsLink={!!briefInfo.get('id')}
          titleTag="h3"
          renderScreenReaderTitle={values => this.renderScreenReaderTitle(values)}
          renderTitleIcon={() => this.renderPrivacyLock()}
          trackAuthorClick={this.trackAuthorClick}
        >
          <ItemFormat bib={bib} />
          {this.renderAvailabilityInfo()}
        </BibBrief>
        <RatingsInfo owner={owner} item={item} shelfName={shelfName} ownerRatingForBib={ownerRatingForBib} />
      </div>
    );
  }
}

ShelfListItemBibBriefInfo.defaultProps = {
  trackContentClick: () => {}
};

ShelfListItemBibBriefInfo.propTypes = {
  currentLibrary: ImmutablePropTypes.map.isRequired,
  bib: bibShape.isRequired,
  item: shelfItemShape.isRequired,
  owner: userIdentityShape.isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfName: PropTypes.string.isRequired,
  ownerRatingForBib: ugcRatingShape,
  handleAvailabilityLinkClick: PropTypes.func,
  trackContentClick: PropTypes.func,
  trackCatalogExplore: PropTypes.func
};

ShelfListItemBibBriefInfo.contextType = Context;
